import {useMemo} from 'react';
import {IOpportunityTokenDefinition, IStorefrontQuestion, IStorefrontSettings} from '../../models/Storefront';
import {
    useGetQuoteRequestQuestionsQuery,
    useGetStorefrontQuestionsQuery
} from '../../store/endpoints/StorefrontQuestionEndpoints';
import {useGetOpportunityTokensQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';

// A storefront opportunity override page maps its extension fields against one of two token contexts:
//  - 'order'        -> storefront order-processor tokens (StorefrontOrderClient.GetTokens) plus per-question
//                      tokens (question_<entityType>_<identifier>[ _text]) for every answered checkout AND
//                      quote-request question (all are written onto the placed order).
//  - 'quoteRequest' -> storefront notification tokens (PreparedOrderDelivery.GetNotificationTokens) plus the
//                      per-question quote-request tokens (quote_request_answer/question:<identifier>).
// The available tokens therefore differ between the "Order Opportunity Details" and "Quote Request Opportunity
// Details" sections; callers pass the matching mode. Backing queries are shared via RTK Query, so the panel and
// the field-level autosuggest that both consume this hook fetch each list only once.
export type OpportunityTokenMode = 'order' | 'quoteRequest';

export type TokenSuggestionGroup = 'General' | 'Questions';

export interface ITokenSuggestion {
    token: string;       // full replacement text, e.g. '{{{order_number}}}'
    description: string;  // human label shown in the panel / autosuggest list
    group: TokenSuggestionGroup;
}

export interface IQuestionTokenEntry {
    key: string;
    label: string;
    answerToken: string;
    questionToken: string;
}

export interface IGeneralTokenEntry {
    key: string;
    token: string;
    description: string;
}

export interface IOpportunityTokens {
    generalTokens: IGeneralTokenEntry[];
    questionEntries: IQuestionTokenEntry[];
    // Flat list (general first, then per-question answer/question tokens) for typeahead insertion.
    suggestions: ITokenSuggestion[];
    generalLoading: boolean;
    questionsLoading: boolean;
}

export function useOpportunityTokens(settings: IStorefrontSettings, mode: OpportunityTokenMode): IOpportunityTokens {
    const storefrontId = settings.id ?? '';

    // Storefront checkout questions (per-storefront ad-hoc + opted-in global pool) — order context only.
    const adHoc = useGetStorefrontQuestionsQuery(storefrontId, {skip: mode !== 'order' || !storefrontId});
    const globalPool = useGetStorefrontQuestionsQuery('global', {skip: mode !== 'order'});
    // Quote-request questions (per-storefront + opted-in global pool). Needed by BOTH contexts: the quote-request
    // context maps them via quote_request_* tokens, and the order context maps them via question_quoteRequest_*
    // tokens because quote-request answers are also written onto the placed order.
    const quoteRequest = useGetQuoteRequestQuestionsQuery(storefrontId, {skip: !storefrontId});
    const globalQuoteRequest = useGetQuoteRequestQuestionsQuery('global');

    const questionsLoading =
        (mode === 'order' && (adHoc.isLoading || adHoc.isFetching || globalPool.isLoading || globalPool.isFetching))
        || quoteRequest.isLoading || quoteRequest.isFetching || globalQuoteRequest.isLoading || globalQuoteRequest.isFetching;

    // General tokens come from the API so the list can't drift from the server-side token builders.
    const opportunityTokens = useGetOpportunityTokensQuery(mode);
    const generalLoading = opportunityTokens.isLoading || opportunityTokens.isFetching;

    const generalTokens: IGeneralTokenEntry[] = useMemo(() => {
        const catalog: IOpportunityTokenDefinition[] = opportunityTokens.data?.items ?? [];
        return catalog
            .filter(t => t.key)
            .map(t => ({key: t.key!, token: `{{{${t.key}}}}`, description: t.description ?? ''}));
    }, [opportunityTokens.data]);

    const questionEntries: IQuestionTokenEntry[] = useMemo(() => {
        // Effective question set for a context = per-storefront ad-hoc + opted-in global pool, deduped by identifier.
        const mergeGlobal = (adHocItems?: IStorefrontQuestion[], globalItems?: IStorefrontQuestion[], selectedIds?: string[]) => {
            const selected = new Set(selectedIds ?? []);
            const merged = [...(adHocItems ?? []), ...((globalItems ?? []).filter(q => q.id && selected.has(q.id)))];
            const seen = new Set<string>();
            return merged.filter(q => !!q.identifier && !seen.has(q.identifier!) && (seen.add(q.identifier!), true));
        };
        // Order path: every answered question is written onto the order as question_<entityType>_<identifier>.
        const orderEntry = (q: IStorefrontQuestion): IQuestionTokenEntry => {
            const id = q.identifier!;
            const entityType = q.entityType || 'storefront';
            return {key: `question_${entityType}_${id}`, label: q.questionText || id,
                answerToken: `{{{question_${entityType}_${id}}}}`, questionToken: `{{{question_${entityType}_${id}_text}}}`};
        };
        // Notification path: quote-request answers are addressed by quote_request_* tokens.
        const quoteEntry = (q: IStorefrontQuestion): IQuestionTokenEntry => {
            const id = q.identifier!;
            return {key: `quote_request:${id}`, label: q.questionText || id,
                answerToken: `{{{quote_request_answer:${id}}}}`, questionToken: `{{{quote_request_question:${id}}}}`};
        };

        const quoteQuestions = mergeGlobal(quoteRequest.data?.items, globalQuoteRequest.data?.items, settings.quoteRequestQuestionIds);
        if (mode === 'quoteRequest') {
            return quoteQuestions.map(quoteEntry);
        }
        // Order context: the storefront checkout questions plus the quote-request questions, all as order tokens.
        const checkoutQuestions = mergeGlobal(adHoc.data?.items, globalPool.data?.items, settings.checkoutQuestionIds);
        return [...checkoutQuestions, ...quoteQuestions].map(orderEntry);
    }, [mode, adHoc.data, globalPool.data, quoteRequest.data, globalQuoteRequest.data, settings.checkoutQuestionIds, settings.quoteRequestQuestionIds]);

    const suggestions: ITokenSuggestion[] = useMemo(() => {
        const general: ITokenSuggestion[] = generalTokens.map(t => ({token: t.token, description: t.description, group: 'General'}));
        const questions: ITokenSuggestion[] = questionEntries.flatMap(e => [
            {token: e.answerToken, description: `${e.label} — Answer`, group: 'Questions'},
            {token: e.questionToken, description: `${e.label} — Question text`, group: 'Questions'}
        ]);
        return [...general, ...questions];
    }, [generalTokens, questionEntries]);

    return {generalTokens, questionEntries, suggestions, generalLoading, questionsLoading};
}
